import React from 'react'
import { FormProvider, SubmitErrorHandler, useForm } from 'react-hook-form'
import { Box, TextField, Typography } from '@mui/material'
import { useSession } from 'next-auth/react'

import { useAppDispatch, useAppSelector } from '#/app/store/store'
import { getStepperInputStyles } from '#/features/register-business/ui/shared/form-styles'
import styles from '#/features/register-business/ui/step-information/step-information.module.scss'
import { ButtonUI } from '#/shared'
import { useShowDataStore } from '#/shared/lib/hooks/use-show-data'

import { contactEmailRules, contactNameRules, contactPhoneRules, contactPositionRules } from '../../lib/constants-step-contact'
import { createBusinessCompany, setContact, setProcessCreate, switchStep } from '../../model/stepper-slice'

type ContactFormValues = {
	name: string
	job_title: string
	email: string
	phone: string
}

export const StepContact: React.FC = () => {
	const dispatch = useAppDispatch()
	const { showMessage } = useShowDataStore()
	const { phone, email, name, job_title } = useAppSelector((state) => state.stepper.dataForCreate)
	const { data: session } = useSession()

	const methods = useForm<ContactFormValues>({
		defaultValues: { phone, email, name, job_title },
	})

	const {
		handleSubmit,
		formState: { errors },
		register,
		reset,
	} = methods

	const handleError: SubmitErrorHandler<ContactFormValues> = (formErrors) => {
		const firstError = Object.values(formErrors)[0]
		if (firstError?.message) {
			showMessage(firstError.message)
		}
	}

	const onSubmit = async (data: ContactFormValues) => {
		dispatch(setContact(data))
		const raw = await dispatch(createBusinessCompany(session?.access ?? null)).unwrap()
		const result = raw as { status: number; data?: { detail?: string } }

		if (result.status === 400 && result.data?.detail) {
			dispatch(setProcessCreate('idle'))
			showMessage(result.data.detail ?? 'Произошла ошибка')
		}

		const emptyValues = { phone: '', email: '', name: '', job_title: '' }
		dispatch(switchStep(0))
		dispatch(setContact(emptyValues))
		reset(emptyValues)
	}

	return (
		<FormProvider {...methods}>
			<form id='step3' className={styles.form} onSubmit={handleSubmit(onSubmit, handleError)}>
				<Box className={styles.formInner}>
					<Box className={styles.element}>
						<Typography>Как вам обращаться?</Typography>
						<TextField
							fullWidth
							sx={getStepperInputStyles(!!errors.name)}
							error={!!errors.name}
							helperText={errors.name?.message}
							{...register('name', contactNameRules)}
						/>
					</Box>

					<Box className={styles.element}>
						<Typography>Ваша должность</Typography>
						<TextField
							fullWidth
							sx={getStepperInputStyles(!!errors.job_title)}
							error={!!errors.job_title}
							helperText={errors.job_title?.message}
							{...register('job_title', contactPositionRules)}
						/>
					</Box>

					<Box className={styles.element}>
						<Typography>Ваш email</Typography>
						<TextField
							fullWidth
							sx={getStepperInputStyles(!!errors.email)}
							error={!!errors.email}
							helperText={errors.email?.message}
							{...register('email', contactEmailRules)}
						/>
					</Box>

					<Box className={styles.element}>
						<Typography>Ваш телефон</Typography>
						<TextField
							fullWidth
							sx={getStepperInputStyles(!!errors.phone)}
							error={!!errors.phone}
							helperText={errors.phone?.message}
							{...register('phone', contactPhoneRules)}
						/>
					</Box>

					<Box className={styles.btn}>
						<ButtonUI onClick={handleSubmit(onSubmit, handleError)} text='Создать' />
					</Box>
				</Box>
			</form>
		</FormProvider>
	)
}
